<template>
  <div v-if="isLoading" class="center-all cloudinary">
    <v-progress-circular size="50" color="primary" indeterminate />
  </div>
  <div v-else-if="errorLoading" class="center-all cloudinary">
    <v-icon>mdi-image</v-icon>
  </div>
  <img
    v-else-if="type === 'image' && cloudinaryImage"
    :src="cloudinaryImage.toURL()"
  />
  <video
    v-else-if="type === 'video' && cloudinaryVideo"
    :src="cloudinaryVideo.toURL()"
  />
</template>

<script setup lang="ts">
import { fill } from '@cloudinary/url-gen/actions/resize'
import {
  Cloudinary,
  CloudinaryImage,
  CloudinaryVideo,
} from '@cloudinary/url-gen'
import { getCloudinaryPublicId, getCloudName } from './helper'

const cloudFolder = import.meta.env.VITE_APP_CLOUD_FOLDER

const { src, type, width, height } = defineProps({
  src: {
    type: String,
    required: true,
  },
  type: {
    type: String,
    default: 'image',
  },
  width: {
    type: [String, Number],
    default: 250,
  },
  height: {
    type: [String, Number],
    default: 250,
  },
})

const currentCloudName = computed(() => getCloudName(src))
const cld = computed(
  () =>
    new Cloudinary({
      cloud: {
        cloudName: currentCloudName.value,
      },
    })
)

const computedWidth = computed(() => `${width}px`)
const computedHeight = computed(() => `${height}px`)
function preloadImages(url: string) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.src = url
    img.onload = resolve
    img.onerror = reject
  })
}

const isLoading = ref(true)
const publicId = computed<string | null>(() =>
  getCloudinaryPublicId(src || '', cloudFolder)
)
const cloudinaryImage = ref<CloudinaryImage | null>(null)
const cloudinaryVideo = ref<CloudinaryVideo | null>(null)

// onMounted(async () => {
// image logic
const loadImage = async () => {
  isLoading.value = true
  if (!publicId.value) {
    isLoading.value = false
    return
  }
  try {
    const image = cld.value.image(publicId.value)
    image.resize(fill().width(width).height(height))
    image.format('webp').quality('auto')
    cloudinaryImage.value = image
    await preloadImages(cloudinaryImage.value.toURL())
    isLoading.value = false
  } catch (e) {
    onError(e)
  }
}

// video logic
const loadVideo = async () => {
  isLoading.value = true
  if (!publicId.value) {
    isLoading.value = false
    return
  }
  try {
    const video = cld.value.video(publicId.value)
    video.resize(fill().width(width).height(height))
    cloudinaryVideo.value = video
    // For video preloading is harder, but we can try basic check or just assume it works
    // await preloadImages(cloudinaryVideo.value.toURL())
    isLoading.value = false
  } catch (e) {
    onError(e)
  }
}
const loadMedia = () => {
  if (type === 'image') {
    loadImage()
  } else {
    loadVideo()
  }
}
watch(
  publicId,
  () => {
    loadMedia()
  },
  { immediate: true }
)
const errorLoading = ref(false)
const onError = (error: any) => {
  console.log(error)
  isLoading.value = false
  errorLoading.value = true
}
onMounted(() => {
  loadMedia()
})
</script>
<style lang="scss" scoped>
.cloudinary {
  width: v-bind(computedWidth) !important;
  height: v-bind(computedHeight) !important;
}
</style>
